import { useMemo } from 'react';
import { isAppModule } from '@src/utils/constants';
import { metricsStore } from '@globalStore/metricsStore';
import { useFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';

const frustrationMapping = {
  b_rage_click: 'b_rage_click',
  b_page_loop: 'b_page_loop',
  b_crash: 'b_crash',
  b_crash_v2: 'b_crash_v2',
  b_anr: 'b_anr',
  b_click_to_error: 'b_click_to_error',
  b_excessive_app_restarts: 'b_excessive_app_restarts',
};
export interface FrustrationSignalMetadata {
  id: string;
  name: string;
  identifier: string;
  metrics: {
    count: string;
    with_user: string;
    with_session: string;
  };
  metaDefinition: {
    dimensions: Array<{
      identifier: string;
      category: string;
    }>;
    metrics: Array<{
      identifier: string;
      type: string;
      output_order?: number;
      category: string;
    }>;
  };
  event_config?: {
    output_event: string;
    is_synthetic: boolean;
  };
}

export function useFrustrationSignalConfigs() {
  const oobRageClicksEnabled = useFeature(featureNames.DPI_OOB_RAGE_CLICKS);
  const pageLoopingEnabled = useFeature(featureNames.DPI_OOB_PAGE_LOOPING);
  const clickToWebErrorEnabled = useFeature(featureNames.DPI_OOB_CLICK_TO_WEB_ERROR);
  const excessiveAppRestartEnabled = useFeature(featureNames.DPI_OOB_EXCESSIVE_APP_RESTART);
  const excludeMobileMetadataEnabled = useFeature(featureNames.DPI_EXCLUDE_MOBILE_META);
  const frustrationSignalConfigMeta = metricsStore((state) => state.frustrationSignalConfigMeta);

  return useMemo<Record<string, FrustrationSignalMetadata>>(() => {
    if (isAppModule && frustrationSignalConfigMeta) {
      // The config meta is already in the correct format
      const filteredConfigMeta = { ...frustrationSignalConfigMeta };
      if (!oobRageClicksEnabled) {
        delete filteredConfigMeta[frustrationMapping.b_rage_click];
      }
      if (!pageLoopingEnabled) {
        delete filteredConfigMeta[frustrationMapping.b_page_loop];
      }
      if (!clickToWebErrorEnabled) {
        delete filteredConfigMeta[frustrationMapping.b_click_to_error];
      }
      if (!excessiveAppRestartEnabled) {
        delete filteredConfigMeta[frustrationMapping.b_excessive_app_restarts];
      }

      if (filteredConfigMeta[frustrationMapping.b_crash]) {
        filteredConfigMeta[frustrationMapping.b_crash].metrics.count = 'AppCrashSessions';
      }

      if (excludeMobileMetadataEnabled) {
        delete filteredConfigMeta[frustrationMapping.b_crash];
        delete filteredConfigMeta[frustrationMapping.b_anr];
        delete filteredConfigMeta[frustrationMapping.b_excessive_app_restarts];
      }

      return filteredConfigMeta;
    }
    return {};
  }, [frustrationSignalConfigMeta]);
}

export function useFrustrationSignalList() {
  const configs = useFrustrationSignalConfigs();

  return useMemo(() => {
    return Object.keys(configs);
  }, [configs]);
}
